import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { useTranslation } from "react-i18next";

import { verifyBooking } from "@/services/eventService";
import { ROUTE } from "@/route/app_routes.js";

/**
 * Mirrors CertificateVerify.js's pattern -- public, unauthenticated lookup by a scanned code,
 * intended for venue staff checking a QR ticket (or typing the booking code) at the door.
 */
const BookingVerify = ({ bookingCode }) => {
  const { t } = useTranslation("events");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState(bookingCode || "");
  const router = useRouter();

  useEffect(() => {
    if (!bookingCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    verifyBooking(bookingCode)
      .then((res) => setResult(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [bookingCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    router.push(`/events/verify/${input.trim()}`);
  };

  return (
    <div className="container ptb--120">
      <div className="row justify-content-center">
        <div className="col-lg-6 text-center">
          <h2 className="title mb--20">{t("verify.title", "Verify an Event Booking")}</h2>
          <p className="description mb--40">
            {t("verify.description", "Enter a booking code to confirm a reserved spot is real and still active.")}
          </p>

          <form onSubmit={handleSubmit} className="rbt-search-style mb--40">
            <input
              type="text"
              placeholder={t("verify.placeholder", "e.g. TKT-XXXXXXXXXX")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button type="submit" className="rbt-search-btn rbt-round-btn">
              <i className="feather-search"></i>
            </button>
          </form>

          {loading && <p>{t("verify.checking", "Checking…")}</p>}

          {!loading && notFound && (
            <div className="rbt-badge-5 bg-color-danger-opacity color-danger">
              {t("verify.notFound", "No booking found with that code.")}
            </div>
          )}

          {!loading && result && (
            <div className="rbt-certificate-card">
              <div className="rbt-certificate-border">
                <span className="rbt-certificate-brand">
                  {result.valid ? t("verify.verified", "Booking Verified ✓") : t("verify.statusPrefix", "Booking {{status}}", { status: result.status })}
                </span>
                <h3 className="mt--20">{result.attendee_name}</h3>
                <p className="mt--10">{t("verify.reservedFor", "reserved a spot for")}</p>
                <h4 className="rbt-certificate-course">{result.event_title}</h4>
                {result.event_date && <p className="mt--20">{result.event_date}</p>}
                {result.event_location && <p>{result.event_location}</p>}
                <p className="rbt-certificate-verify">{t("verify.bookingCode", "Booking Code {{code}}", { code: result.booking_code })}</p>
              </div>
            </div>
          )}

          <div className="mt--40">
            <Link className="rbt-btn-link" href={ROUTE.events}>
              {t("verify.browseEvents", "Browse Events")} <i className="feather-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingVerify;
